// =============================================================================
// app/src/lib/diaDoc.ts — "O DIA NA LOJA", a folha que se lê de longe.
//
// Pedido do dono (19/08/2026): "um documento na aba de relatórios, bonito e
// simples de ver, do que aconteceu no dia — para meu pai, que não é de
// tecnologia, saber tudo o que fizemos hoje: pedidos, vendas, etc."
//
// PARA QUEM ESTE PAPEL É ESCRITO, e o que isso muda
//
// O leitor não usa o sistema, não vai clicar em nada e provavelmente vai ler
// sem óculos, à noite, na mesa da cozinha. Então:
//
//   * fonte grande (o corpo aqui é 13,5px contra os 10-11px do vale) e UM
//     número gigante — quanto a loja vendeu. Se ele ler só isso, já valeu;
//   * palavra de gente, não de sistema: "o que vendemos", "o que pagamos",
//     "a receber". Nada de "movimentos", "RPC", "SKU" ou "conta a receber";
//   * o que NÃO aconteceu não aparece. Dia sem encomenda não imprime um bloco
//     de encomendas dizendo zero — folha com zeros ensina a não ler a folha;
//   * uma página só. O agente de impressão fotografa exatamente um A4, e o que
//     passa é cortado; por isso as listas têm teto e dizem "e mais N".
//
// Segurança igual à do vale e da encomenda (mesmo motivo — a janela nasce de
// about:blank e herda a origem do app): todo texto vindo do banco passa por
// `escaparHtml`, e o documento declara CSP `script-src 'none'`.
// =============================================================================
import type { ResumoDoDia } from '@core/types';
import { brl, qtd } from './formato';
import { escaparHtml } from './aVerDoc';

// Exportados (22/09/2026): a lista de contas a pagar escreve as datas com as
// mesmas palavras, sem uma segunda cópia dos nomes.
export const DIAS = [
  'domingo', 'segunda-feira', 'terça-feira', 'quarta-feira',
  'quinta-feira', 'sexta-feira', 'sábado',
];
export const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

/**
 * "Quarta-feira, 19 de agosto de 2026" — por extenso, porque é assim que se
 * lê uma data em voz alta.
 *
 * Meio-dia na conversão: `new Date('2026-08-19')` é meia-noite UTC e vira o
 * DIA ANTERIOR em Rio Grande. Um relatório com a data errada é um relatório
 * que ninguém confere duas vezes.
 */
export function diaPorExtenso(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const nome = DIAS[d.getDay()]!;
  return `${nome.charAt(0).toUpperCase()}${nome.slice(1)}, ${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
}

// Os tetos existem para a folha caber num A4 (medido no navegador a 794x1123:
// com 14 peças ela ia a 1332 px e o agente cortava o rodapé). Lista maior
// vira "e mais N".
const TETO_PECAS = 7;
const TETO_LISTA = 8;

/** O nome que a forma de pagamento tem NA BOCA das pessoas. O banco guarda
 *  'a_ver' e 'debito'; ninguém fala assim, e esta folha é para ser lida. */
const FORMA_EM_PORTUGUES: Record<string, string> = {
  dinheiro: 'Dinheiro',
  pix: 'PIX',
  debito: 'Cartão de débito',
  credito: 'Cartão de crédito',
  transferencia: 'Transferência',
  boleto: 'Boleto',
  a_ver: 'Vale "a ver"',
  fiado: 'Fiado',
};
const nomeDaForma = (f: string) => FORMA_EM_PORTUGUES[f] ?? f.replace(/_/g, ' ');

// O SÍMBOLO SAI EM PRETO NO PAPEL, e isso é decisão de operação (20/08/2026).
// Na tela e no site ele é o vermelho da marca (#D90429, regra 10). Aqui não:
// UM pixel colorido faz a HP jato de tinta trocar para o modo em cores, e a
// página que sai em segundos passa a levar quase um minuto — com o cliente
// esperando no balcão. O agente ainda manda `print-color-mode: monochrome` por
// IPP; este preto é o cinto além do suspensório (documento que a loja imprime
// fora do agente também sai rápido).
const S_LOGO = `<svg viewBox="0 0 738 502" width="42" style="vertical-align:middle" xmlns="http://www.w3.org/2000/svg"><path fill="#111111" d="M265.16 197.096L716.099 197.133C710.828 236.013 703.583 277.477 697.547 316.427L691.319 357.303C688.727 374.581 687.153 388.315 681.173 404.926C673.717 426.092 661.62 445.323 645.765 461.208C626.687 480.431 602.358 493.601 575.825 499.063C558.852 502.518 534.196 501.482 516.359 501.482L0 501.506C6.45146 483.313 20.0753 438.942 29.209 424.596C33.8706 417.227 39.9357 410.846 47.0599 405.817C68.5016 390.473 90.0878 393.236 114.993 393.246L467.663 393.298C480.383 393.309 491.548 393.616 505.058 391.956C536.745 388.419 573.091 341.625 550.341 312.787C542.231 302.501 520.482 305.103 508.558 305.102L240.273 305.049L265.16 197.096Z"/><path fill="#111111" d="M195.821 0.543453C213.607 -0.496412 240.032 0.274996 258.347 0.279316L737.602 0.30894L726.382 34.5152C715.525 67.7255 704.255 100.888 663.231 107.702C652.029 109.563 638.877 108.711 627.422 108.697L297.094 108.625C283.826 108.599 258.16 107.708 246.463 110.265C234.27 113.024 222.913 118.655 213.336 126.689C202.159 136.104 193.793 148.416 189.157 162.274C184.055 177.316 183.118 204.508 181.773 221.171L174.91 305.148L27.0847 305.094C27.9432 296.569 30.2866 284.133 31.7117 275.497L41.2102 218.07L50.7884 158.008C53.5367 140.681 55.7487 122.604 61.4157 106.006C68.0344 86.9096 78.1359 69.2054 91.208 53.7901C117.493 22.6886 155.205 3.49334 195.821 0.543453Z"/></svg>`;

export interface OpcoesDiaDoc {
  /** Momento da impressão — injetável para o teste. */
  agora?: Date;
}

const horaCurta = (d: Date) =>
  `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

function bloco(titulo: string, corpo: string): string {
  return corpo
    ? `<section><h2>${escaparHtml(titulo)}</h2>${corpo}</section>`
    : '';
}

function linhas(
  itens: Array<{ esquerda: string; direita: string }>,
  teto: number,
  /** ["saída", "saídas"] — plural certo importa numa folha para ler. */
  oQue: [string, string],
  total?: { rotulo: string; valor: string },
): string {
  if (itens.length === 0) return '';
  const visiveis = itens.slice(0, teto);
  const resto = itens.length - visiveis.length;
  return `<table>${visiveis.map((i) => `<tr>
      <td>${escaparHtml(i.esquerda)}</td>
      <td class="dir">${escaparHtml(i.direita)}</td>
    </tr>`).join('')}${resto > 0
    ? `<tr><td colspan="2" class="mais">e mais ${resto} ${escaparHtml(resto === 1 ? oQue[0] : oQue[1])}</td></tr>`
    : ''}${total
    ? `<tr class="destaque"><td>${escaparHtml(total.rotulo)}</td>
         <td class="dir">${escaparHtml(total.valor)}</td></tr>`
    : ''}</table>`;
}

/** Monta a folha. Separado da impressão para poder ser testado — e é o MESMO
 *  HTML que vai para a fila da impressora da loja. */
export function montarDocumentoDoDia(r: ResumoDoDia, opcoes: OpcoesDiaDoc = {}): string {
  const agora = opcoes.agora ?? new Date();
  const v = r.vendas;
  const c = r.caixa;

  // O dinheiro que ENTROU de verdade separado do que foi vendido: é a
  // distinção que o fiado cria, e a que mais confunde quem lê de fora.
  const aReceber = Number(v.fiado);

  const formas = r.por_forma
    .filter((f) => f.forma !== 'fiado')
    .map((f) => ({
      esquerda: `${nomeDaForma(f.forma)} — ${f.quantidade} venda${f.quantidade > 1 ? 's' : ''}`,
      direita: brl(Number(f.total)),
    }));

  const vendedores = r.por_vendedor.map((p) => ({
    esquerda: `${p.vendedor} — ${p.quantidade} venda${p.quantidade > 1 ? 's' : ''}`,
    direita: brl(Number(p.total)),
  }));

  const pecas = r.pecas.map((p) => ({
    esquerda: `${qtd(p.quantidade)} × ${p.descricao}`,
    direita: brl(Number(p.total)),
  }));

  const entradas = r.entradas.map((e) => ({
    esquerda: e.quantidade > 1 ? `${e.origem} (${e.quantidade})` : e.origem,
    direita: brl(Number(e.valor)),
  }));

  const saidas = r.saidas.map((s) => ({
    esquerda: s.descricao === '—' ? s.categoria : `${s.descricao} — ${s.categoria}`,
    direita: `− ${brl(Number(s.valor))}`,
  }));

  const pedidos = r.pedidos.map((p) => ({
    esquerda: `${p.fornecedor} — pedido nº ${p.numero}`,
    direita: `${p.itens} ${p.itens === 1 ? 'peça' : 'peças'}`,
  }));

  const enc = r.encomendas;
  const encomendasTexto = [
    enc.novas > 0 ? `${enc.novas} nova${enc.novas > 1 ? 's' : ''}` : '',
    enc.entregues > 0 ? `${enc.entregues} entregue${enc.entregues > 1 ? 's' : ''}` : '',
    Number(enc.recebido) > 0 ? `${brl(Number(enc.recebido))} recebidos` : '',
  ].filter(Boolean).join(' · ');

  const g = r.gaveta;
  const gaveta = g === null ? '' : `<table>
      <tr><td>Começou o dia com</td><td class="dir">${brl(Number(g.fundo))}</td></tr>
      ${g.fechada ? `
      <tr><td>Contamos no fim do dia</td><td class="dir">${brl(Number(g.contado ?? 0))}</td></tr>
      <tr><td>O sistema esperava</td><td class="dir">${brl(Number(g.esperado ?? 0))}</td></tr>
      <tr class="destaque"><td>${Number(g.diferenca) >= 0 ? 'Sobrou' : 'Faltou'}</td>
          <td class="dir">${brl(Math.abs(Number(g.diferenca ?? 0)))}</td></tr>`
      : '<tr><td colspan="2" class="mais">O caixa ainda não foi fechado.</td></tr>'}
    </table>`;

  return `<!doctype html>
<html lang="pt-BR"><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy"
      content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; script-src 'none'; form-action 'none'">
<title>O dia na loja — ${escaparHtml(r.dia)}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  /* Uma página A4 (1123 px CSS). O agente fotografa exatamente isso e corta o
     resto — por isso as listas têm teto. Fonte grande de propósito: quem lê
     esta folha não usa o sistema e não vai apertar o zoom de nada. */
  body { font-family: Arial, Helvetica, sans-serif; color: #111; padding: 7mm; font-size: 13.5px; }
  header { display: flex; justify-content: space-between; align-items: center;
           border-bottom: 3px solid #111; padding-bottom: 3mm; margin-bottom: 4mm; }
  .marca .nome { font-weight: 900; font-style: italic; font-size: 22px; margin-left: 7px; }
  .marca .sub { font-size: 10px; letter-spacing: 2px; margin-left: 7px; color: #555; }
  .doc { text-align: right; }
  .doc .titulo { display: block; font-size: 12px; letter-spacing: 3px; }
  .doc .data { display: block; font-weight: 700; font-size: 15px; }
  .placar { display: flex; gap: 4mm; margin-bottom: 3.5mm; }
  .placar .caixa-num { flex: 1; border: 2px solid #111; padding: 3mm 4mm; }
  .placar .caixa-num.forte { background: #111; color: #fff; flex: 1.6; }
  .placar .rot { display: block; font-size: 11px; letter-spacing: 1.5px;
                 text-transform: uppercase; margin-bottom: 1mm; }
  .placar .num { display: block; font-size: 30px; font-weight: 900; line-height: 1.1; }
  .placar .caixa-num.forte .num { font-size: 38px; }
  .placar .obs { display: block; font-size: 11px; margin-top: 1mm; }
  section { margin-bottom: 2.4mm; break-inside: avoid; }
  h2 { font-size: 12px; letter-spacing: 2px; text-transform: uppercase;
       border-bottom: 1px solid #111; padding-bottom: 1mm; margin-bottom: 1.5mm; }
  table { width: 100%; border-collapse: collapse; }
  td { padding: 0.9mm 0; border-bottom: 1px solid #e6e6e6; }
  td.dir { text-align: right; font-weight: 700; white-space: nowrap; }
  tr.destaque td { font-weight: 900; border-bottom: none; }
  .mais { color: #666; font-size: 11.5px; }
  .nota { font-size: 12px; color: #333; margin-top: 1.2mm; }
  .frase { font-size: 13.5px; }
  .rodape { margin-top: 4mm; border-top: 1px solid #111; padding-top: 2mm;
            font-size: 10.5px; color: #666; display: flex; justify-content: space-between; }
  @media print { body { padding: 0; } }
</style></head>
<body>
  <header>
    <div class="marca">${S_LOGO}<span class="nome">SERGINHO</span><span class="sub">AUTO PEÇAS</span></div>
    <div class="doc">
      <span class="titulo">O DIA NA LOJA</span>
      <span class="data">${escaparHtml(diaPorExtenso(r.dia))}</span>
    </div>
  </header>

  <div class="placar">
    <div class="caixa-num forte">
      <span class="rot">Vendemos hoje</span>
      <span class="num">${brl(Number(v.total))}</span>
      <span class="obs">em ${v.quantidade} venda${v.quantidade === 1 ? '' : 's'}${
        v.canceladas > 0 ? ` · ${v.canceladas} cancelada${v.canceladas > 1 ? 's' : ''}` : ''
      }</span>
    </div>
    <div class="caixa-num">
      <span class="rot">Entrou no caixa</span>
      <span class="num">${brl(Number(c.entrou))}</span>
      <span class="obs">${brl(Number(c.entrou_dinheiro))} em dinheiro</span>
    </div>
    <div class="caixa-num">
      <span class="rot">Saiu do caixa</span>
      <span class="num">${brl(Number(c.saiu))}</span>
      <span class="obs">contas, compras, retiradas</span>
    </div>
  </div>

  ${aReceber > 0 ? `<section class="frase"><strong>${brl(aReceber)}</strong> foi vendido
    <strong>fiado</strong> — é venda feita, mas o dinheiro ainda não entrou.</section>` : ''}

  ${bloco('Dinheiro que ENTROU', linhas(entradas, TETO_LISTA, ['origem', 'origens'],
    // Com uma origem só, o total repetiria a única linha — e linha repetida
    // faz quem lê procurar a diferença que não existe.
    entradas.length > 1 ? { rotulo: 'Total que entrou', valor: brl(Number(c.entrou)) } : undefined)
    + (formas.length > 0
      ? `<p class="nota">Nas vendas, os clientes pagaram assim: ${formas
          .map((f) => `${escaparHtml(f.esquerda.split(' — ')[0]!)} ${escaparHtml(f.direita)}`)
          .join(' · ')}</p>`
      : ''))}
  ${bloco('Dinheiro que SAIU', linhas(saidas, TETO_LISTA, ['saída', 'saídas'],
    saidas.length > 1 ? { rotulo: 'Total que saiu', valor: `− ${brl(Number(c.saiu))}` } : undefined))}
  ${bloco('Quem vendeu', linhas(vendedores, TETO_LISTA, ['vendedor', 'vendedores']))}
  ${bloco('As peças que saíram da loja', linhas(pecas, TETO_PECAS, ['peça diferente', 'peças diferentes']))}
  ${bloco('A gaveta do caixa', gaveta)}
  ${bloco('Pedidos aos fornecedores', linhas(pedidos, TETO_LISTA, ['pedido', 'pedidos']))}
  ${encomendasTexto
    ? bloco('Encomendas', `<p class="frase">${escaparHtml(encomendasTexto)}</p>`) : ''}
  ${Number(r.orcamentos.quantidade) > 0
    ? bloco('Orçamentos feitos', `<p class="frase">${r.orcamentos.quantidade} orçamento${
        Number(r.orcamentos.quantidade) > 1 ? 's' : ''} · ${brl(Number(r.orcamentos.total))}</p>`)
    : ''}
  ${Number(r.fiado.recebido_hoje) > 0 || Number(r.fiado.em_aberto) > 0
    ? bloco('Fiado', `<table>
        ${Number(r.fiado.recebido_hoje) > 0
          ? `<tr><td>Clientes acertaram hoje</td><td class="dir">${brl(Number(r.fiado.recebido_hoje))}</td></tr>` : ''}
        <tr><td>Ainda temos a receber</td><td class="dir">${brl(Number(r.fiado.em_aberto))}</td></tr>
      </table>`)
    : ''}

  <p class="rodape">
    <span>Serginho Autopeças · Rio Grande/RS</span>
    <span>impresso às ${escaparHtml(horaCurta(agora))}</span>
  </p>
</body></html>`;
}

export function imprimirDia(r: ResumoDoDia, opcoes: OpcoesDiaDoc = {}): void {
  const janela = window.open('', '_blank', 'width=900,height=1000');
  if (!janela) return;
  janela.document.write(montarDocumentoDoDia(r, opcoes));
  janela.document.close();
  janela.focus();
  janela.print();
}
